import * as React from 'react';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { FavoriteToggle } from '../../common/react/components/FavoriteToggle/FavoriteToggle';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ListResult } from '../../ts/fetchers/ListResult';
import { IMetricInfo } from '../../ts/models/IMetricInfo';
import { IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo } from '../../ts/models/resourceInfoParts';
import { DataSources } from '../../ts/resources/Datasources';
import { Favorites } from '../../ts/resources/Favorites';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { ExploreContentApi_ContentType } from '../../ts/ServerApi/types/IContent';
import { IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { IMetricWithSummaryData } from '../../ts/ServerApi/types/IMetricWithSummaryData';
import { IWorkbookActions } from '../../ts/ServerApi/types/IWorkbookActions';
import { Telemetry } from '../../ts/services/TelemetryService';
import { DevicePreviewModalBuilder } from '../content/devicePreview/DevicePreviewModalBuilder';
import { VizViewerWidgetsBar } from '../content/VizViewerWidgetsBar';
import { ServiceLocator } from '../ServiceLocator';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { AvailableActions } from '../state/AvailableActionsForEntityState';
import { LayoutType } from '../state/LayoutType';
import { SpaceInfo } from '../state/SpaceInfo';
import { PageHeaderStyles } from './PageHeaderStyles';

export interface VizViewerPageHeaderButtonsProps extends ReactDirectiveProps {
  readonly spaceInfo: SpaceInfo;
  readonly siteUrls: SpacesSiteUrls;
  readonly workbookActions: AvailableActions;
  readonly getWorkbookActions: (workbook: IExplorableItemTypeInfo) => void;
  readonly telemetryService: Telemetry;
  readonly datasourcesResource: DataSources;
  readonly layoutType: LayoutType;
}

export class VizViewerPageHeaderButtons extends React.PureComponent<VizViewerPageHeaderButtonsProps> {
  public componentDidUpdate() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo) && !this.props.workbookActions) {
      const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;
      viewInfo.workbook && this.props.getWorkbookActions({ ...viewInfo.workbook, contentType: 'workbook' });
    }
  }

  public render() {
    if (!isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // don't render this until we get the view info back
      return null;
    }

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    return (
      <div className={PageHeaderStyles.VizViewerButtons}>
        <VizViewerWidgetsBar
          view={viewInfo}
          siteUrls={this.props.siteUrls}
          dataSources={this.props.datasourcesResource}
          showDevicePreview={FeatureFlags.PreviewDeviceLayouts
            && this.props.workbookActions
            && (this.props.workbookActions as IWorkbookActions).launchWebAuthoring}
          handleDevicePreviewClick={this.handleDevicePreviewClick}
          layoutType={this.props.layoutType}
          telemetryService={this.props.telemetryService}
        />
      </div>);
  }

  private handleDevicePreviewClick = () => {
    this.props.telemetryService.sendEvent<DevicePreviewShowPreviewEvent>({
      type: TelemetryEvents.DevicePreviewShowPreview
    });

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    DevicePreviewModalBuilder.openPreview(this.props.angularContext, viewInfo.path);
  }
}


/**
 * Common between Viz Viewer and Metric Detail Page
 */
// show a unfavorited button until we get the view info back
const favoriteToggleProps: FavoriteToggle.Props = {
  isFavorite: false,
  onAddFavorite: _.noop,
  onRemoveFavorite: _.noop,
  canHaveKeyboardFocus: true,
  buttonType: 'light'
};

/**
 * Viz Viewer Page Favorite Button
 */
function isValidSpaceInfoForVizView(spaceInfo: SpaceInfo) {
  // First, run.ts changes the NavLocation in the state. That means the NavLocation is 'viz' before
  // we have updated the space info with the correct item. So we need to check that the content type is correct.
  // Second, VizViewCtrl nullifies the space info's item until it gets the view info back, so we need to
  // check that the item is not null before using it.
  return (spaceInfo.item && spaceInfo.item.contentType === 'view');
}

export interface VizViewerFavoriteButtonProps {
  readonly spaceInfo: SpaceInfo;
  readonly addContentFavorite: (contentId: string, contentType: ExploreContentApi_ContentType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: ExploreContentApi_ContentType, contentName: string) => void;
}

export class VizViewerFavoriteButton extends React.PureComponent<VizViewerFavoriteButtonProps> {

  public render() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // show a unfavorited button until we get the view info back
      const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
      favoriteToggleProps.isFavorite = viewInfo.favorite;
      favoriteToggleProps.onAddFavorite = this.onAddFavorite;
      favoriteToggleProps.onRemoveFavorite = this.onRemoveFavorite;
    }

    return (
      <div style={{marginLeft: '-2px', ...CommonStyles.Layout.FlexBlockLeftAndCenter}}>
        <FavoriteToggle {...favoriteToggleProps} />
      </div>
    );
  }

  private onAddFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.addContentFavorite(viewInfo.id, 'view');
  }

  private onRemoveFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.removeContentFavorite(viewInfo.id, viewInfo.id, 'view', viewInfo.name);
  }
}

// 2019.4 Metrics
/**
 * Metrics Detail Page Favorite Button
 */
function isValidSpaceInfoForMetricDetail(spaceInfo: SpaceInfo) {
  return (spaceInfo.item && spaceInfo.item.contentType === 'metric');
}

export interface MetricDetailFavoriteButtonProps {
  readonly spaceInfo: SpaceInfo;
  readonly addContentFavorite: (contentId: string, contentType: ExploreContentApi_ContentType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: ExploreContentApi_ContentType, contentName: string) => void;
}

export interface MetricDetailFavoriteButtonState {
  readonly isFavorite: boolean;
}

export class MetricDetailFavoriteButton extends React.PureComponent<MetricDetailFavoriteButtonProps, MetricDetailFavoriteButtonState> {

  private unmounted: boolean = false;
  private foundIsFavorite: boolean = false;

  constructor(props: MetricDetailFavoriteButtonProps) {
    super(props);
    this.state = {isFavorite: false};
  }

  public componentDidMount() {
    this.findAndSetMetricIsFavoritedItem();
  }

  public componentDidUpdate() {
    this.findAndSetMetricIsFavoritedItem();
  }

  private async findAndSetMetricIsFavoritedItem() {
    if (!this.foundIsFavorite) {
      const metricInfo: IMetricWithSummaryData = this.props.spaceInfo.item as any as IMetricWithSummaryData;
      if (!metricInfo.metric) {
        // metric info hasn't been populated yet so return;
        return;
      }

      // set foundIsFavorite to true so we don't find and set favorite again
      this.foundIsFavorite = true;

      // get all metric favorites
      const favoriteMetricsResource = ServiceLocator.get<Favorites>('Favorites');
      const favoriteResourceQuery: ResourceQuery<IFavorite> = favoriteMetricsResource.allMetrics();
      const favoritesResult: ListResult<IFavorite> = await favoriteResourceQuery.getSlice(0, 1000);

      // find if the metric is a favorited item
      const metricId: string = metricInfo.metric.id;
      const isFavoritedItem: boolean = _.any(favoritesResult.items, (favoriteItem: IFavorite) => {
        return favoriteItem.objectId === String(metricId);
      });

      // set the favorite state
      this.updateState({isFavorite: isFavoritedItem});
    }
  }

  private updateState<K extends keyof MetricDetailFavoriteButtonState>(newState: Pick<MetricDetailFavoriteButtonState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public render() {
    if (isValidSpaceInfoForMetricDetail(this.props.spaceInfo)) {
      // show a unfavorited button until we get the metric info back
      const metricInfo: IMetricWithSummaryData = this.props.spaceInfo.item as any as IMetricWithSummaryData;
      favoriteToggleProps.isFavorite = this.state.isFavorite;
      favoriteToggleProps.onAddFavorite = this.onAddFavorite;
      favoriteToggleProps.onRemoveFavorite = this.onRemoveFavorite;
    }

    return (
      <div style={{marginLeft: '-2px', ...CommonStyles.Layout.FlexBlockLeftAndCenter}}>
        <FavoriteToggle {...favoriteToggleProps} />
      </div>
    );
  }

  private onAddFavorite = () => {
    const metricInfo: IMetricWithSummaryData = this.props.spaceInfo.item as any as IMetricWithSummaryData;
    this.props.addContentFavorite(metricInfo.metric.id, 'metric');
  }

  private onRemoveFavorite = () => {
    const metricInfo: IMetricWithSummaryData = this.props.spaceInfo.item as any as IMetricWithSummaryData;
    this.props.removeContentFavorite(metricInfo.metric.id, metricInfo.metric.id, 'metric', metricInfo.metric.name);
  }
}
